import { days as dayCount, moved } from "@nabla/api";
import type { Fig, NoteDto, Window } from "@nabla/api";
import { designCopy } from "../../../design/copy.ts";
import { formatPeriod, zoneSuffix } from "../../../design/format/dates.ts";
import { formatFrequency, formatNumber, formatPercent, plural } from "../../../design/format/numbers.ts";
import { days, stamp, type NoteMarker } from "../../../words/coverage.ts";
import { figureNumber, goalNumber } from "../../../words/figures.ts";
import { wordsCopy } from "../../../words/copy.ts";
import { metaNamed, sourceNamed, type Words } from "../../../words/session.ts";
import { levelCopy as t } from "../copy.ts";
import { amount } from "../../../design/format/money.ts";

type Tone = "positivo" | "negativo" | "neutro";

export function formulaOf(fig: Fig, words: Words): string | undefined {
  if (fig.source.kind !== "nabla") return undefined;
  const formula = designCopy.formula[fig.source.formula];
  return typeof formula === "function" ? formula(words.resultado.varios) : formula;
}

export const unitsOf = (n: number): string => `${formatNumber(n)} ${plural(n, t.unidade.um, t.unidade.varios)}`;

export function sharedReasons(figures: readonly Fig[], words: Words): string[] {
  const reasons = figures.flatMap((fig) => (figureNumber(fig) === undefined ? [missingReason(fig, words)] : []));
  return reasons.filter((reason, at) => reasons.indexOf(reason) === at && reasons.lastIndexOf(reason) !== at);
}

export function missingReason(fig: Fig, words: Words): string {
  const code = fig.missing ?? "sem-dados";
  if (code === "fonte-sem-campanha") return t.semValor["fonte-sem-campanha"]((words.fonte ?? metaNamed).A);
  if (code === "fusos-diferentes" || code === "sem-anuncios" || code === "sem-fonte") return t.semValor[code];
  return designCopy.semValor[code];
}

interface Comparison {
  readonly arrow: "▲" | "▼";
  readonly percent: string;
  readonly tone: Tone;
  readonly days: number;
}

export function comparisonOf(fig: Fig): Comparison | undefined {
  const { previous } = fig;
  const change = moved(fig, previous)?.share;
  if (previous === undefined || change === undefined) return undefined;
  const percent = formatPercent(Math.abs(change), { whole: true });
  if (percent === formatPercent(0, { whole: true })) return undefined;
  const better = fig.goal?.better;
  const good = better === "maior" ? change > 0 : better === "menor" ? change < 0 : undefined;
  return { arrow: change > 0 ? "▲" : "▼", percent, tone: good === undefined ? "neutro" : good ? "positivo" : "negativo", days: dayCount(previous.window) };
}

export function primaryOf(figures: readonly Fig[]): Fig | undefined {
  return figures.find((fig) => fig.goal !== undefined) ?? figures.find((fig) => fig.metric !== "investido") ?? figures[0];
}

type Hero = { readonly primary: Fig | undefined; readonly figures: Fig[]; readonly more: Fig[] };

export function heroOf(figures: readonly Fig[], more: readonly Fig[]): Hero {
  const primary = primaryOf([...figures, ...more]);
  const others = (figs: readonly Fig[]) => figs.filter((fig) => fig !== primary);
  return { primary, figures: others(figures), more: others(more) };
}

export function againstGoal(fig: Fig, brief = false): { readonly text: string; readonly tone: Tone } | undefined {
  const goal = fig.goal;
  if (!goal || goal.status === "sem-dado" || fig.value === null) return undefined;
  const target = brief ? undefined : goalNumber(goal);
  const said = (text: string, tone: Tone) => ({ text: goal.basis === undefined ? text : t.campanha.semImposto(text), tone });
  if (goal.status === "dentro") return said(t.campanha.dentro(target), goal.basis === undefined ? "positivo" : "neutro");
  const times = fig.value / goal.target;
  if (times >= 2) return said(t.campanha.vezes(formatFrequency(times), target), "negativo");
  return said(t.campanha.fora(formatPercent(Math.abs(times - 1)), times > 1, target), "negativo");
}

export function originOf(window: Window, words: Words, sales: boolean): string {
  const sources = sales && words.fonte ? t.origem.metaE(words.fonte.a) : wordsCopy.meta;
  return t.origem.janela(sources, formatPeriod(window.from, window.to, words.today), zoneSuffix(window.timezone));
}

export function noteText(note: NoteDto, words: Words, level: "empresa" | "conta" | "campanha" | "conjunto" | "anuncio"): { readonly text: string; readonly marker?: NoteMarker } | undefined {
  switch (note.code) {
    case "dias-incompletos":
      return { text: wordsCopy.nota.incompletos(days(note.days, words), note.days.length > 1), marker: "incompleto" };
    case "dias-faltando":
      return { text: note.source.kind === "meta" ? wordsCopy.nota.faltandoMeta(days(note.days, words), note.days.length) : wordsCopy.nota.faltando(sourceNamed(note.source, words).A, days(note.days, words), note.days.length), marker: "incompleto" };
    case "dados-desatualizados":
      return { text: designCopy.alerta["dados-atrasados"](sourceNamed(note.source, words).da, stamp(note.at, words)), marker: "atencao" };
    case "dias-amadurecendo":
      return { text: t.nota.amadurecendo };
    case "vendas-outros-anuncios":
      return { text: t.nota.outrosAnuncios(amount(note.amount)) };
    case "vendas-sem-anuncio":
      return { text: level === "campanha" || level === "conta" || level === "empresa" ? t.nota.semAnuncio(amount(note.amount)) : t.nota.semAnuncioAbaixo };
    case "sem-anuncios-listados":
      return { text: t.semValor["sem-anuncios"] };
    case "imposto-desconhecido":
      return { text: t.semValor["imposto-desconhecido"] };
    case "fonte-sem-campanha":
      return { text: t.semValor["fonte-sem-campanha"](sourceNamed(note.source, words).A) };
    case "so-utm":
      return { text: t.nota.soUtm };
    case "sem-meta":
      return { text: t.nota.semLeituraDoMeta, marker: "atencao" };
    case "definicao-da-fonte":
      return { text: t.nota.definicao[note.source.definition] };
    case "filhos-somam-menos":
    case "sem-fonte":
    case "fuso-da-conta":
      return undefined;
  }
}
